import { getOrderDiscountBreakdown, getOrderRevenueBreakdown } from "@fitness-app/data-models/entities/Order";
import { type ProductPrice } from "@fitness-app/data-models/entities/Product";

const DAY_MS = 24 * 60 * 60 * 1000;

export type SalesPerPriceOrder = Parameters<typeof getOrderRevenueBreakdown>[0] &
  Parameters<typeof getOrderDiscountBreakdown>[0] & {
    id: string;
    createdAt: string | null;
    priceId?: string | null;
    clientEmail?: string | null;
    /** Money sent back; only a refund with an amount counts, a failed attempt (error, no amount) does not. */
    refund?: object | null;
  };

/** An archived client of the product, as the resignation side of a sale. */
export interface SalesPerPriceClient {
  email: string;
  connectedOrders: string[] | null;
  archivedInfo: { archivedAt: number; reason: string } | null;
  lastOrder: (Parameters<typeof getOrderRevenueBreakdown>[0] & { paymentCycle: number | null }) | null;
}

export interface SalesPerPriceRow {
  priceId: string;
  label: string;
  currency: string;
  transactions: number;
  /** First purchases: orders without the `renewal` metadata flag. */
  newPurchases: number;
  /** Next-cycle charges of an existing plan. */
  renewals: number;
  /** Orders charged 0 — a 100% code, a free extension, a manual comp. */
  free: number;
  /** Actually charged, in cents. */
  paid: number;
  /** First payment plus every scheduled installment, in cents. */
  projected: number;
  /** Orders with money sent back, and how much, in cents. */
  refunds: number;
  refundedAmount: number;
  /** Buyers who resigned: archived with a refund, or whose installment plan stopped before its end. */
  resignations: number;
  /** Average days from the purchase to the resignation, null without resignations. */
  resignationAvgDays: number | null;
  /** Share of all transactions in the range, 0–1. */
  share: number;
}

export type SalesPerPriceTotals = Pick<
  SalesPerPriceRow,
  | "transactions"
  | "newPurchases"
  | "renewals"
  | "free"
  | "paid"
  | "projected"
  | "refunds"
  | "refundedAmount"
  | "resignations"
  | "resignationAvgDays"
>;

export interface SalesPerPriceSummary {
  rows: SalesPerPriceRow[];
  totals: SalesPerPriceTotals;
  currency: string;
}

type Accumulator = Omit<SalesPerPriceRow, "share" | "resignationAvgDays"> & { resignationDays: number[] };

const emptyAccumulator = (): Omit<Accumulator, "priceId" | "label" | "currency"> => ({
  transactions: 0,
  newPurchases: 0,
  renewals: 0,
  free: 0,
  paid: 0,
  projected: 0,
  refunds: 0,
  refundedAmount: 0,
  resignations: 0,
  resignationDays: [],
});

/** Whole days from the purchase to the archive; null when either side is missing or the archive predates the purchase. */
const daysBetween = (purchasedAt: string | null, archivedAtUnix: number | undefined): number | null => {
  if (!purchasedAt || !archivedAtUnix) {
    return null;
  }
  const elapsed = archivedAtUnix * 1000 - new Date(purchasedAt).getTime();
  return elapsed >= 0 ? Math.round(elapsed / DAY_MS) : null;
};

const refundedAmountOf = (refund: object | null | undefined): number =>
  refund && "amount" in refund && typeof refund.amount === "number" ? refund.amount : 0;

const average = (values: number[]) =>
  values.length ? Math.round(values.reduce((acc, value) => acc + value, 0) / values.length) : null;

/**
 * Whether an archived client resigned rather than ran the plan to its end: the panel archived her
 * with a refund, or her installment plan stopped before the last installment.
 */
const isResignation = (client: SalesPerPriceClient): boolean => {
  if (!client.archivedInfo) {
    return false;
  }

  if (client.archivedInfo.reason === "refund") {
    return true;
  }

  const lastOrder = client.lastOrder;

  if (!lastOrder || lastOrder.paymentCycle === null) {
    return false;
  }

  const { installments } = getOrderRevenueBreakdown(lastOrder);

  return installments > 1 && lastOrder.paymentCycle < installments;
};

/** Index resignations by the order ids they are linked to and by e-mail, for orders older than the link. */
const indexResignations = (clients: SalesPerPriceClient[]) => {
  const byOrderId = new Map<string, SalesPerPriceClient>();
  const byEmail = new Map<string, SalesPerPriceClient>();

  for (const client of clients) {
    if (!isResignation(client)) {
      continue;
    }
    for (const orderId of client.connectedOrders ?? []) {
      byOrderId.set(orderId, client);
    }
    byEmail.set(client.email.toLowerCase(), client);
  }

  return (order: SalesPerPriceOrder) =>
    byOrderId.get(order.id) ?? (order.clientEmail ? byEmail.get(order.clientEmail.toLowerCase()) : undefined);
};

/**
 * How many of each package (price) sold in the range, what the buyers paid, and how many of them
 * asked for their money back or stopped paying. Orders outside the selected variants' prices are
 * skipped, like in the revenue donuts, so every block of the tab counts the same set. Rows come
 * most-sold first.
 */
export const aggregateSalesPerPrice = (
  orders: SalesPerPriceOrder[],
  prices: Record<string, ProductPrice>,
  labelOf: (price: ProductPrice, priceId: string) => string,
  archivedClients: SalesPerPriceClient[] = [],
): SalesPerPriceSummary => {
  const byPrice = new Map<string, Accumulator>();
  const totals: Omit<Accumulator, "priceId" | "label" | "currency"> = emptyAccumulator();
  const resignationOf = indexResignations(archivedClients);

  for (const order of orders) {
    const priceId = order.priceId;
    const price = priceId ? prices[priceId] : undefined;

    if (!priceId || !price) {
      continue;
    }

    const { paid, projected } = getOrderRevenueBreakdown(order);
    const { isFree } = getOrderDiscountBreakdown(order);
    const isRenewal = order.metadata?.renewal === true;
    const refundedAmount = refundedAmountOf(order.refund);
    const resignation = resignationOf(order);
    const resignationDays = resignation ? daysBetween(order.createdAt, resignation.archivedInfo?.archivedAt) : null;

    const row = byPrice.get(priceId) ?? {
      priceId,
      label: labelOf(price, priceId),
      currency: price.currency ?? "pln",
      ...emptyAccumulator(),
    };

    for (const target of [row, totals]) {
      target.transactions += 1;
      target.newPurchases += isRenewal ? 0 : 1;
      target.renewals += isRenewal ? 1 : 0;
      target.free += isFree ? 1 : 0;
      target.paid += paid;
      target.projected += projected;
      target.refunds += refundedAmount > 0 ? 1 : 0;
      target.refundedAmount += refundedAmount;
      if (resignation) {
        target.resignations += 1;
        if (resignationDays !== null) {
          target.resignationDays.push(resignationDays);
        }
      }
    }

    byPrice.set(priceId, row);
  }

  const finish = ({ resignationDays, ...row }: typeof totals) => ({
    ...row,
    resignationAvgDays: average(resignationDays),
  });

  const rows = [...byPrice.values()]
    .sort((a, b) => b.transactions - a.transactions || b.paid - a.paid || a.label.localeCompare(b.label, "pl"))
    .map((row) => ({
      ...finish(row),
      priceId: row.priceId,
      label: row.label,
      currency: row.currency,
      share: totals.transactions ? row.transactions / totals.transactions : 0,
    }));

  return { rows, totals: finish(totals), currency: rows[0]?.currency ?? "pln" };
};
